import React, { cloneElement, createContext, PropsWithChildren, ReactElement, useCallback, useContext, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, } from 'react';

//style
import './form-list-base.css';

//devExtreme
import "devextreme-react/radio-group";
import { Button } from "devextreme-react";
import { Popup } from "devextreme-react/popup";
import { ToolbarRef } from "devextreme-react/toolbar";
import { TabPanelRef } from "devextreme-react/tab-panel";
import { Resizable, ResizableRef } from "devextreme-react/resizable";
import { FocusedRowChangedEvent } from "devextreme/ui/data_grid";

//components
import { FormListBaseGrid, FormListBaseTab, FormListTabProduct } from './index';
import FormListBaseToolbar, { IFromToolbarProps } from "./form-list-base-toolbar";

//models
import IDataSource from "../../models/IDataSource";
import { DataGridRef } from "devextreme-react/data-grid";
import { useStorage } from "../../hooks/useStorage";
import { Mode } from "../../hooks/useCustomDataSource";
import { useContextPopupPage } from "../../components/popups/PopupPage";
import { InitializedEvent, ResizeEndEvent } from "devextreme/ui/resizable";


interface FromListBaseProps extends PropsWithChildren {
    data: IDataSource<any, any>,
    listeInitialState?: 'ouvert' | 'cacher'
    formName?: string,
    modeSelection?: boolean,
    hideToolbar?: boolean,
    hideList?: boolean,
    saveBtnRef?: any,
    width?: string,
    isModeSelection?: boolean,
}

interface IListBaseSize {
    listeSize: number,
    setListeSize: (value: number) => void
}

const FormListBaseSizeContext = createContext<IListBaseSize>({
    listeSize: 0,
    setListeSize: value => {

    }
});

const useFormlistBaseSizeContext = () => useContext(FormListBaseSizeContext);

const FormListBase = (props: FromListBaseProps) => {
    const { data, children, listeInitialState, formName, modeSelection, hideToolbar, hideList, saveBtnRef, width = '400px', isModeSelection = false } = props;
    const { isPopupOpen } = useContextPopupPage();
    const isSelectionMode = Boolean(isModeSelection || modeSelection || isPopupOpen);
    const {
        //methods
        ajouter,
        annuler,
        sauvegarder,
        effacer,
        mode,
        //value
        dataSource,
        isFieldModifier,
        selectedId,
        forceFocusOnSelectedId,
        setMode
    } = data;

    //ref
    const toolbarRef = useRef<ToolbarRef>(null);
    const tabPanelRef = useRef<TabPanelRef>(null);
    const tabPanelRefProduct = useRef<TabPanelRef>(null);
    const resizableDivRef = useRef<HTMLDivElement>(null);
    const resizableProductRef = useRef<HTMLDivElement>(null);
    const dataGridRef = useRef<DataGridRef>(null);
    const [listeSize, setListeSizeState] = useStorage<string>({ key: formName ? `liste-size-${formName}` : "liste-size", storageType: "localStorage", defaultValue: width, expirationInMilliseconds: -1 })
    const setListeSize = (value: string) => {
        setListeSizeState(value);
    }

    //state
    const [cacherListe, setCacherListe] = useState(listeInitialState == "cacher");

    const focusedRowChanged = useCallback((e: FocusedRowChangedEvent) => {
        const key = e.row?.key;
        if (!key) {
            return;
        }

        dataSource?.store()?.byKey(key); // cette ligne modifie la source de donnees avec la cle de la ligne courante dans FormListBaseGrid (panneau a gauche)
        //  --->> consequence : le hooks personnalise useCustomDataSource execute son useEffect qui a la dependance dataStore pour
    }, [dataSource]);

    const focusedRowChangedModeSelection = useCallback((e: FocusedRowChangedEvent) => {
        const key = e.row?.key;
        if (!key) {
            return;
        }

        if (mode !== Mode.Lecture) {
            return;
        }

        dataSource?.store()?.byKey(key); // cette ligne modifie la source de donnees avec la cle de la ligne courante dans FormListBaseGrid (panneau a gauche)
        //  --->> consequence : le hooks personnalise useCustomDataSource execute son useEffect qui a la dependance dataStore pour
    }, [dataSource, mode]);


    const dataGrid = useMemo(() => React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === FormListBaseGrid) {
            const childElement = child as ReactElement<any>
            const isEditingInSelection = isSelectionMode && mode !== Mode.Lecture;
            const props = {
                ref: dataGridRef,
                onFocusedRowChanged: (e: FocusedRowChangedEvent) => {
                    isSelectionMode
                        ? focusedRowChangedModeSelection(e)
                        : focusedRowChanged(e);
                    if (!isEditingInSelection && childElement.props.onFocusedRowChanged) {
                        childElement.props.onFocusedRowChanged(e);
                    }
                },
                dataSource: childElement.props.dataSource || data.dataSource
            }

            return cloneElement(child, {
                ...childElement.props,
                ...props,
                ...(forceFocusOnSelectedId ? { focusedRowKey: selectedId } : {})
            });
        }

        return;
    }), [children, dataGridRef, focusedRowChangedModeSelection, focusedRowChanged, forceFocusOnSelectedId, selectedId, data.dataSource, isSelectionMode, mode])

    const tabPanel = useMemo(() => React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === FormListBaseTab) {
            //custum panel Tab here
            const childElement = child as ReactElement<any>
            return cloneElement(child, { ...childElement.props, ref: tabPanelRef });
        }
        return;
    }), [children, tabPanelRef])

    function toggleModeModify() {
        setMode(prev => prev === Mode.Lecture ? Mode.Modifier : Mode.Lecture);
    }

    const toolbar = useMemo(() => React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === FormListBaseToolbar) {
            const childElement = child as ReactElement<any>
            return cloneElement(child,
                {
                    ...childElement.props,
                    modifierOptions: {
                        click: () => {
                            //dataSource?.reload();
                            toggleModeModify();
                        },
                        disabled: mode !== Mode.Lecture || isFieldModifier
                    },
                    sauvegarderOptions: {
                        ...childElement.props.sauvegarderOptions, click: (e: any) => {
                            if (childElement.props.sauvegarderOptions?.click) {
                                childElement.props.sauvegarderOptions.click(e)
                            }

                            if (sauvegarder && !e.preventDefault) {
                                sauvegarder(e);
                            }

                        }, disabled: !isFieldModifier
                    },
                    ajouterOptions: {
                        ...childElement.props.ajouterOptions,
                        click: (e: any) => {
                            if (childElement.props.ajouterOptions?.click) {
                                childElement.props.ajouterOptions.click(e);
                            }

                            if (ajouter && !e.preventDefault) {
                                ajouter();
                            }
                        }
                    },
                    effacerOptions: {
                        ...childElement.props.effacerOptions, click: async (e: any) => {
                            let result = false;
                            if (childElement.props.effacerOptions?.click) {
                                result = result && childElement.props.effacerOptions.click(e);
                            }
                            if (effacer && !e.preventDefault) {
                                result = await effacer();
                            }
                            return result
                        }
                    },
                    annulerOptions: {
                        ...childElement.props.annulerOptions,
                        click: (e: any) => {
                            if (childElement.props.annulerOptions?.click) {
                                childElement.props.annulerOptions.click(e);
                            }
                            if (annuler && !e.preventDefault) {
                                annuler();
                            }
                        }, disabled: !isFieldModifier && mode === Mode.Lecture
                    },
                    dataSource,
                    ref: toolbarRef
                } as IFromToolbarProps);
        }
        return;
    }
    ) ||
        [], [children, dataSource, isFieldModifier, mode, toolbarRef, sauvegarder, ajouter, effacer, annuler])

    const otherChildren = useMemo(() => React.Children.map(children, (child) => {
        if (React.isValidElement(child) && (child.type === FormListBaseToolbar || child.type === FormListTabProduct || child.type === FormListBaseTab || child.type === FormListBaseGrid)) return null;

        return child;
    }), [children]);


    if (toolbar && toolbar.length === 0) {
        toolbar.push(<FormListBaseToolbar ref={toolbarRef} {...{
            modifierOptions: {
                click: () => {
                    //dataSource?.reload();
                    toggleModeModify();
                },
                disabled: mode !== Mode.Lecture || isFieldModifier
            },
            sauvegarderOptions: {
                click: (e: any) => {
                    if (sauvegarder && !e.preventDefault) {
                        sauvegarder(e);
                    }
                }, disabled: !isFieldModifier
            },
            ajouterOptions: {
                click: (e: any) => {
                    if (ajouter && !e.preventDefault) {
                        ajouter();
                    }
                }
            },
            effacerOptions: {
                click: async (e: any) => {
                    let result = false;
                    if (effacer && !e.preventDefault) {
                        result = await effacer();
                    }
                    return result
                }
            },
            annulerOptions: {
                click: (e: any) => {
                    if (annuler && !e.preventDefault) {
                        annuler();
                    }
                }, disabled: !isFieldModifier && mode === Mode.Lecture
            },
            dataSource
        } as IFromToolbarProps} />);
    }

    const refreshToolbar = useCallback(() => {
        toolbarRef.current?.instance().repaint();
    }, []);

    useLayoutEffect(() => {
        const refreshtimer = setTimeout(refreshToolbar, 500)

        return () => clearTimeout(refreshtimer);
    }, [cacherListe])

    const collapseBaronClick = useCallback(async () => {
        await setCacherListe(prev => !prev)
    }, []);

    const cacherListeStyle = useMemo(() => ({
        fontSize: "18px"
    }), []);

    const onResizeStart = useCallback(() => {
        if (resizableDivRef.current) {
            resizableDivRef.current.className += " resizing";
            dataGridRef.current?.instance().beginUpdate();
            tabPanelRef.current?.instance().beginUpdate();
            tabPanelRefProduct.current?.instance().beginUpdate();
            toolbarRef.current?.instance().beginUpdate();
        }
    }, [resizableDivRef.current, dataGridRef.current, tabPanelRef.current, toolbarRef.current, tabPanelRefProduct.current])

    const onResizeEnd = useCallback((e: ResizeEndEvent) => {
        if (!modeSelection)
            setListeSize(`${e.width}px`);
        toolbarRef.current?.instance().repaint();
        tabPanelRef.current?.instance().repaint();
        tabPanelRef.current?.instance().endUpdate();
        toolbarRef.current?.instance().endUpdate();
        tabPanelRefProduct.current?.instance().repaint();
        tabPanelRefProduct.current?.instance().endUpdate();
        dataGridRef.current?.instance().repaint();
        dataGridRef.current?.instance().endUpdate();
        if (resizableDivRef.current)
            resizableDivRef.current.className = resizableDivRef.current.className.replace(" resizing", "").trim();
    }, [resizableDivRef.current, dataGridRef.current, tabPanelRef.current, toolbarRef.current, tabPanelRefProduct.current, modeSelection]);
    const resizabledRef = useRef<ResizableRef>(null);
    const onInitialized = useCallback((e: InitializedEvent) => {
        e?.component?.option("width", listeSize);
    }, [listeSize]);
    return (
        <div className={"base-form-content"}>
            <Popup>
                <div></div>
                <Button>Oui</Button>
                <Button>Non</Button>
            </Popup>
            {dataGrid && !hideList ?  /* check if dataGrid exist */
                <div ref={resizableDivRef} className={`form-list ${cacherListe ? "collapsed" : ""}`}> {/* if dataGrid exist return this block */}
                    {(tabPanel && (tabPanel.length > 0 || otherChildren && otherChildren.length > 0) && (!modeSelection || mode != Mode.Lecture)) ? /* check if tabPanel exist and if he isn't empty */
                        <>
                            {/* resize Grid */}
                            <Resizable
                                ref={resizabledRef}
                                handles={"right"}
                                height={"100%"}
                                onResizeStart={onResizeStart}
                                onResizeEnd={onResizeEnd}
                                onInitialized={onInitialized}
                            >
                                {dataGrid}
                            </Resizable>
                            <div className={"collapse-bar"} onClick={
                                collapseBaronClick
                            }>
                                <i className={`dx-icon-${cacherListe ? "spinright" : "spinleft"}`} style={cacherListeStyle} />
                                <div>{`${cacherListe ? 'Afficher' : 'Cacher'} la liste`}</div>
                            </div>
                        </>
                        : /* if tabPanel don't exist or if he is empty  return this block*/
                        <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                            <div>{toolbar}</div>
                            <div style={{ flex: 1, overflow: "auto", height: "100%" }}>{dataGrid}</div>
                        </div>
                    }
                </div> :
                null /* if dataGrid don't exist return null*/
            }
            {tabPanel && tabPanel.length > 0 || otherChildren && otherChildren.length > 0 ?
                <div className="form-left-content">
                    {(!modeSelection || mode !== Mode.Lecture) && !hideToolbar ? toolbar : null}
                    <div style={{ display: "flex", height: '100%', paddingTop: "1rem", flex: "1 0 0", overflow: "auto" }}>
                        {tabPanel && tabPanel.length > 0 && (!modeSelection || mode !== Mode.Lecture) ? tabPanel : null}
                        {otherChildren && otherChildren.length > 0 ? otherChildren : null}
                    </div>
                </div> : null}
        </div>
    )
}

export { FormListBase, useFormlistBaseSizeContext }